/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars, capitalized-comments */
'use client';

import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import Link from 'next/link';
import { getStore } from '~/mobx/AppStore';
import { ElectionMainMenu, MenuItem } from '~/definitions/election';
import { TAB_META } from '~/consts/tab';

interface ElectionMenuProps {
    electionMenu: ElectionMainMenu | null;
    handleSlagClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    handleRegionClick: (region: any) => (e: React.MouseEvent<HTMLAnchorElement>) => void;
    selectedElectionMenu: MenuItem | null;
}

const ElectionMenu: FC<ElectionMenuProps> = (props: ElectionMenuProps) => {
    const { electionMenu, handleSlagClick, handleRegionClick, selectedElectionMenu } = props;

    const basedLinkClassName =
        'relative block px-4 py-2 no-underline border-t border-[#d9d9d9] text-[#333] leading-[20px]';

    // Link classes
    const sLagLinkClassName = `${basedLinkClassName} font-bold py-[5px] px-[15px]`;
    const regionLinkClassName = `${basedLinkClassName} text-[100%] py-[5px] px-[25px]`;

    // Hover classes
    const hoverArrowClass = [
        'hover:text-[#0a58ca]',
        'hover:bg-custom-gray-04',
        'hover:before:content-[""]',
        'hover:before:absolute',
        'hover:before:left-0',
        'hover:before:top-1/2',
        'hover:before:-translate-y-1/2',
        'hover:before:border-y-[5px]',
        'hover:before:border-l-[6px]',
        'hover:before:border-y-transparent',
        'hover:before:border-l-[#82AB34]',
    ].join(' ');

    // Active link classes
    const activeArrowClass = [
        'before:content-[""]',
        'before:absolute',
        'before:left-0',
        'before:top-1/2',
        'before:-translate-y-1/2',
        'before:border-y-[5px]',
        'before:border-l-[6px]',
        'before:border-y-transparent',
        'before:border-l-[#82AB34]',
    ].join(' ');
    const sLagLinActiveLinkClassName = `bg-[#f9f9f9] font-bold`;
    const regionActiveLinkClassName = `bg-[#f9f9f9]`;

    const renderSlagMenu = () => {
        const noSelection = !selectedElectionMenu;
        const isSlagMenuActive = noSelection || selectedElectionMenu?.id === electionMenu?.id;

        const linkClass = [
            sLagLinkClassName,
            isSlagMenuActive ? `${sLagLinActiveLinkClassName} ${activeArrowClass}` : '',
            hoverArrowClass,
        ]
            .filter(Boolean)
            .join(' ');

        return (
            electionMenu && (
                <Link href={electionMenu?.href || ''} onClick={handleSlagClick} className={linkClass}>
                    {electionMenu?.text}
                </Link>
            )
        );
    };

    const renderRegionMenu = () => {
        if (!electionMenu?.regionList?.length) return null;

        return (
            <ul className={'ml-0'}>
                {electionMenu?.regionList.map((region) => {
                    const isActive = selectedElectionMenu?.id === region.id;

                    const linkClass = [
                        regionLinkClassName,
                        isActive ? `${regionActiveLinkClassName} ${activeArrowClass}` : '',
                        hoverArrowClass,
                    ]
                        .filter(Boolean)
                        .join(' ');

                    return (
                        <li key={region.id}>
                            <Link href={region.href} onClick={handleRegionClick(region)} className={linkClass}>
                                {region.text}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div id="election-menu" data-test-id="election-menu">
            <div className={'mb-4 font-bold'}>Valgportal hovedmeny</div>
            <ul className={'w-full'}>
                <li>
                    {/* First level */}
                    {renderSlagMenu()}
                    {/* Sub-menu */}
                    {renderRegionMenu()}
                </li>
            </ul>
        </div>
    );
};

const ElectionMenuWithStore = () => {
    const {
        electionMenuStore,
        companyStore,
        zipcodeStore,
        regionStore,
        regionPreviewStore,
        generalSettingsStore,
        candidateStore,
        tabStore,
        reportStore,
    } = getStore();
    const electionMenu = electionMenuStore.getElectionMenu();
    if (electionMenu) {
        electionMenuStore.setElectionMenu(electionMenu);
    }

    const handleSlagClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        reportStore.setVotesList([]);

        const { activeTabId } = tabStore;
        // If coming from Candidates or Postal Code tab, mark company level as pre-selected
        if (activeTabId === TAB_META.CANDIDATES.id || activeTabId === TAB_META.POSTAL_CODE.id) {
            electionMenuStore.setIsCompanyLevelPreSelected(true);
        } else {
            if (electionMenu) {
                electionMenuStore.setSelectedElectionMenu(electionMenu);
            }
            regionStore.setSelectedRegionId('');
            electionMenuStore.setIsCompanyLevel(true);
        }

        switch (activeTabId) {
            case TAB_META.GENERAL_SETTINGS.id:
                await generalSettingsStore.getElectionById();
                break;
            case TAB_META.REPORT.id:
                await reportStore.handleFetchReportData();
                break;
            case TAB_META.CANDIDATES.id:
                await candidateStore.getCandidateListByRegionId(regionStore.selectedRegionId);
                break;
            case TAB_META.POSTAL_CODE.id:
                await zipcodeStore.getZipcodeListAndPagination();
                break;
        }
    };

    const handleRegionClick = (region: any) => async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        electionMenuStore.setSelectedElectionMenu(region);
        electionMenuStore.setIsCompanyLevel(false);
        electionMenuStore.setIsCompanyLevelPreSelected(false);
        regionStore.setSelectedRegionId(region.id);
        zipcodeStore.setSelectedRangeId('');
        reportStore.setIsShowedUserVoteList(false);

        switch (tabStore.activeTabId) {
            case TAB_META.GENERAL_SETTINGS.id:
                await generalSettingsStore.getRegionById();
                break;
            case TAB_META.CANDIDATES.id:
                await candidateStore.getCandidateListByRegionId(region.id);
                candidateStore.setIsEditMode(false);
                candidateStore.setSelectedCandidateId('');
                break;
            case TAB_META.POSTAL_CODE.id:
                await zipcodeStore.getZipcodeListAndPagination();
                break;
            case TAB_META.PREVIEW.id:
                regionPreviewStore.changeSelectedRegion(region.id);
                break;
            case TAB_META.REPORT.id:
                await reportStore.handleFetchReportData();
                break;
        }
    };

    useEffect(() => {
        electionMenuStore.getElections();
    }, [companyStore.companyList, companyStore.selectedSlagId]);

    useEffect(() => {
        electionMenuStore.getRegions();
    }, [generalSettingsStore.selectedElectionId]);

    return (
        <ElectionMenu
            electionMenu={electionMenu}
            handleSlagClick={handleSlagClick}
            handleRegionClick={handleRegionClick}
            selectedElectionMenu={electionMenuStore.selectedElectionMenu}
        />
    );
};

export default observer(ElectionMenuWithStore);
